import arrayFrom from 'array-from';
import { addClass, removeClass } from '../../tools/helpers';

/**
 * @fileoverview FilterListController
 *
 * Features:
 * - Pagination ("load more") driven by data-items-per-page + data-current-page per container
 * - Load-more button only if there are more (filtered) items than currently visible
 * - Restores paging from URL parameter on page load
 * - Observes SimpleFilter (#simple-filter) and filters items by data-filter-id
 * - Comparison ignores responsive suffixes "-isDesktop" / "-isMobile"
 */

/**
 * Parse an integer safely.
 * @param {*} value
 * @param {number} fallback
 * @returns {number}
 */
const toInt = (value, fallback) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
};

/**
 * Clamp an integer to a minimum value.
 * @param {number} value
 * @param {number} min
 * @returns {number}
 */
const clampMinInt = (value, min) => {
  const n = toInt(value, min);
  return n < min ? min : n;
};

/**
 * Normalize filter ids to ignore responsive suffixes.
 * Examples:
 * - "abc-isDesktop" -> "abc"
 * - "select-all-isMobile" -> "select-all"
 *
 * @param {string} id
 * @returns {string}
 */
const normalizeResponsiveSuffix = id => String(id || '').replace(/-(isDesktop|isMobile)\b/g, '');

/**
 * URL query param utility.
 */
class UrlParamUtil {
  /**
   * Read an int query param.
   * Returns fallback if param is not present or invalid.
   *
   * @param {string} name
   * @param {?number} fallback
   * @returns {?number}
   */
  static getInt(name, fallback) {
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.has(name)) {
        return fallback;
      }

      return toInt(params.get(name), fallback);
    } catch (error) {
      return fallback;
    }
  }

  /**
   * Set/delete an int query param.
   * Deletes param if value is null/undefined.
   *
   * @param {string} name
   * @param {?number} valueOrNull - null/undefined deletes the param
   * @returns {void}
   */
  static setInt(name, valueOrNull) {
    try {
      const params = new URLSearchParams(window.location.search);

      if (valueOrNull === null || valueOrNull === undefined) {
        params.delete(name);
      } else {
        params.set(name, String(valueOrNull));
      }

      const query = params.toString();
      const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
      window.history.replaceState(null, '', url);
    } catch (error) {
      // no-op
    }
  }
}

/**
 * FilterListController
 *
 * Expected markup (per container):
 * - .js-filter-list-container[data-items-per-page][data-current-page][data-filter-elements-class-name]
 * - inside it: #filter-list (or .filter-list) containing items with class from data-filter-elements-class-name
 * - items have data-filter-id
 *
 * It observes #simple-filter and matches:
 * - active filter button data-id (normalized) vs item data-filter-id (normalized)
 */
class FilterListController {
  /**
   * @param {Object} options
   * @param {HTMLElement} options.element - container element (.js-filter-list-container)
   */
  constructor(options = {}) {
    /** @type {HTMLElement} */
    this.element = options.element;

    /** @type {number} */
    this.itemsPerPage = 1;

    /** @type {number} */
    this.currentPage = 1;

    /**
     * URL parameter name for paging.
     * Note: if you later need multiple containers per page, this should become container-specific.
     * @type {string}
     */
    this.URL_PARAM_PAGE = 'filterListPage';

    /** @type {?HTMLElement} */
    this.filterRoot = null;

    /** @type {?MutationObserver} */
    this.filterObserver = null;

    /** @type {string[]} */
    this.activeFilterIds = [];

    /** @type {?HTMLElement} */
    this.filterListEl = null;

    /** @type {string} */
    this.itemClassName = '';

    /** @type {HTMLElement[]} */
    this.allItems = [];

    /** @type {HTMLElement[]} */
    this.domAllItems = [];

    /** @type {?HTMLButtonElement} */
    this.loadMoreBtn = null;

    /** @type {Function} */
    this.onLoadMoreClick = this.onLoadMoreClick.bind(this);

    /** @type {Function} */
    this.onObservedFilterMutations = this.onObservedFilterMutations.bind(this);

    /** @type {Function} */
    this.onSimpleFilterClick = this.onSimpleFilterClick.bind(this);

    /** @type {?number} */
    this.pendingFilterUpdateTimer = null;
  }

  /**
   * Initialize controller.
   * @returns {void}
   */
  init() {
    if (!this.element) {
      return;
    }

    this.itemsPerPage = clampMinInt(this.element.dataset.itemsPerPage, 1);
    this.currentPage = clampMinInt(this.element.dataset.currentPage, 1);

    this.itemClassName = this.element.dataset.filterElementsClassName || '';
    this.filterMatchMode = this.element.dataset.filterMatchMode || 'single';
    this.filterListEl = this.element.querySelector('#filter-list') || this.element.querySelector('.filter-list');

    if (!this.filterListEl || !this.itemClassName) {
      return;
    }

    this.filterRoot = document.querySelector('#simple-filter');

    // Restore current page from URL (if present)
    const urlPage = UrlParamUtil.getInt(this.URL_PARAM_PAGE, null);
    if (urlPage !== null && urlPage >= 1) {
      this.currentPage = urlPage;
    }

    this.setCurrentPage(this.currentPage, { syncDataset: true, syncUrl: false });
    this.cacheItems();
    this.ensureLoadMoreButton();
    this.bindFilterObservation();
    this.updateFromFiltersAndPaging({ syncUrl: true });
  }

  /**
   * Cache all filter items inside the list.
   * - domAllItems: alle Cards im Container (für "alles verstecken")
   * - allItems: nur die Cards dieser Liste (kommend/verg.) in korrekter Sortierung
   * - schreibt eine konsistente Reihenfolge in den DOM (Sortierung ist wichtiger als Filter)
   *
   * @returns {void}
   */
  cacheItems() {
    const all = arrayFrom(this.filterListEl.querySelectorAll(`.${this.itemClassName}`));
    this.domAllItems = all;

    const getTimeValue = (cardEl) => {
      const timeEl =
        cardEl.querySelector('.mol--termine-card__header--date') ||
        cardEl.querySelector('.mol--press-release__date time') ||
        cardEl.querySelector('time[datetime]');
      const datetime = timeEl ? timeEl.getAttribute('datetime') : null;
      const t = datetime ? Date.parse(datetime) : NaN;
      return Number.isNaN(t) ? NaN : t;
    };

    const getStartOfTodayTs = () => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    const todayTs = getStartOfTodayTs();
    const sortDirection = String(this.element.dataset.sortDirection || '').trim(); // "toNew" | "toOld" | "latestFirst"
    const isValid = el => !Number.isNaN(getTimeValue(el));
    const isUpcoming = el => getTimeValue(el) >= todayTs; // heute zählt als "kommend"
    const isPast = el => getTimeValue(el) < todayTs;

    // 1. Scope (nur für diese Liste)
    let scoped = all.filter(isValid);
    if (sortDirection === 'toNew') {
      scoped = scoped.filter(isUpcoming);
    } else if (sortDirection === 'toOld') {
      scoped = scoped.filter(isPast);
    } else if (sortDirection === 'latestFirst') {
      scoped = scoped.filter(isValid);
    }

    // 2. Sortierung (Filter hat KEINEN Einfluss auf Reihenfolge)
    if (sortDirection === 'toNew') {
      // kommende: aufsteigend (nächster zuerst)
      scoped.sort((a, b) => getTimeValue(a) - getTimeValue(b));
    } else if (sortDirection === 'toOld') {
      // vergangene: absteigend (zuletzt vergangener zuerst)
      scoped.sort((a, b) => getTimeValue(b) - getTimeValue(a));
    } else if (sortDirection === 'latestFirst') {
      /* neueste oben */
      scoped.sort((a, b) => getTimeValue(b) - getTimeValue(a));
      /* === ÄNDERUNG ENDE === */
    } else {
      // Fallback: neueste oben
      scoped.sort((a, b) => getTimeValue(b) - getTimeValue(a));
    }

    // 3. DOM-Reihenfolge stabilisieren:
    // Erst alle gescopten/sortierten rein, danach den Rest (wird sowieso ausgeblendet)
    const scopedSet = new Set(scoped);
    const remainder = all.filter(el => !scopedSet.has(el));

    scoped.concat(remainder).forEach((el) => {
      this.filterListEl.appendChild(el);
    });

    // Controller arbeitet ab jetzt für Filter+Paging nur auf scoped (sortiert!)
    this.allItems = scoped;
  }

  /**
   * Ensure load more button exists and is bound.
   * @returns {void}
   */
  ensureLoadMoreButton() {
    /** @type {?HTMLButtonElement} */
    this.loadMoreBtn = this.element.querySelector('.js-filter-list-load-more');

    if (!this.loadMoreBtn) {
      const wrapper = document.createElement('div');
      wrapper.className = 'more-button container';

      const row = document.createElement('div');
      row.className = 'row';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn-primary js-filter-list-load-more';
      btn.textContent = 'Mehr anzeigen';

      row.appendChild(btn);
      wrapper.appendChild(row);
      this.element.appendChild(wrapper);

      this.loadMoreBtn = btn;
    }

    this.loadMoreBtn.removeEventListener('click', this.onLoadMoreClick);
    this.loadMoreBtn.addEventListener('click', this.onLoadMoreClick);
  }

  /**
   * Bind observation/listeners to SimpleFilter.
   * - MutationObserver for ".isActive" toggles
   * - Click fallback
   *
   * @returns {void}
   */
  bindFilterObservation() {
    if (this.filterRoot) {
      this.filterRoot.removeEventListener('click', this.onSimpleFilterClick);
      this.filterRoot.addEventListener('click', this.onSimpleFilterClick);
    }

    if (!this.filterRoot || !window.MutationObserver) {
      return;
    }

    this.filterObserver = new MutationObserver(this.onObservedFilterMutations);
    this.filterObserver.observe(this.filterRoot, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /**
   * Handle clicks within SimpleFilter.
   * @returns {void}
   */
  onSimpleFilterClick() {
    this.scheduleExternalFilterChange();
  }

  /**
   * Handle MutationObserver updates from SimpleFilter.
   * @param {MutationRecord[]} mutations
   * @returns {void}
   */
  onObservedFilterMutations(mutations) {
    const shouldUpdate = mutations.some(m => m.type === 'attributes');
    if (shouldUpdate) {
      this.scheduleExternalFilterChange();
    }
  }

  /**
   * Debounced handler for external filter changes.
   * This prevents multiple rapid MutationObserver triggers from causing repeated work.
   * @returns {void}
   */
  scheduleExternalFilterChange() {
    if (this.pendingFilterUpdateTimer) {
      window.clearTimeout(this.pendingFilterUpdateTimer);
    }

    this.pendingFilterUpdateTimer = window.setTimeout(() => {
      this.pendingFilterUpdateTimer = null;
      this.onExternalFilterChange();
    }, 0);
  }

  /**
   * Handle filter changes:
   * - Reset paging to page 1
   * - Sync URL param
   * - Re-apply filter + paging visibility
   *
   * @returns {void}
   */
  onExternalFilterChange() {
    this.setCurrentPage(1, { syncDataset: true, syncUrl: true });
    this.updateFromFiltersAndPaging({ syncUrl: true });
  }

  /**
   * Set current page with optional syncing.
   * @param {number} nextPage
   * @param {Object} options
   * @param {boolean} options.syncDataset
   * @param {boolean} options.syncUrl
   * @returns {void}
   */
  setCurrentPage(nextPage, { syncDataset, syncUrl } = {}) {
    this.currentPage = clampMinInt(nextPage, 1);

    if (syncDataset) {
      this.element.dataset.currentPage = String(this.currentPage);
    }

    if (syncUrl) {
      UrlParamUtil.setInt(this.URL_PARAM_PAGE, this.currentPage);
    }
  }

  /**
   * Load-more click handler: increments page, updates visibility, and syncs URL.
   * @returns {void}
   */
  onLoadMoreClick() {
    this.setCurrentPage(this.currentPage + 1, { syncDataset: true, syncUrl: true });
    this.updateFromFiltersAndPaging({ syncUrl: false });
  }

  /**
   * Update internal filter state and apply visibility.
   * @param {Object} options
   * @param {boolean} options.syncUrl
   * @returns {void}
   */
  updateFromFiltersAndPaging({ syncUrl } = {}) {
    this.activeFilterIds = this.getActiveCategoryFilterIds();
    this.cacheItems();

    const filteredItems = this.getFilteredItems();
    const visibleCount = this.getVisibleCount();

    this.applyVisibility(filteredItems, visibleCount);
    this.updateLoadMoreVisibility(filteredItems.length, visibleCount);

    if (syncUrl) {
      UrlParamUtil.setInt(this.URL_PARAM_PAGE, this.currentPage);
    }
  }

  /**
   * Read active filter ids from SimpleFilter.
   * Rules:
   * - Uses ".category-filter.isActive"
   * - Uses button "data-id" (or id fallback)
   * - Normalizes "-isDesktop/-isMobile"
   * - If "select-all" is active => returns empty array (means "show all")
   *
   * @returns {string[]}
   */
  getActiveCategoryFilterIds() {
    if (!this.filterRoot) {
      return [];
    }

    const activeButtons = arrayFrom(this.filterRoot.querySelectorAll('.category-filter.isActive'));
    const ids = activeButtons
      .map(btn => normalizeResponsiveSuffix(btn.getAttribute('data-id') || btn.id))
      .filter(Boolean);

    const hasSelectAll = ids.some(id => id === 'select-all');
    if (hasSelectAll) {
      return [];
    }

    // Dedupe
    return ids.reduce((acc, id) => {
      if (acc.indexOf(id) === -1) {
        acc.push(id);
      }

      return acc;
    }, []);
  }

  /**
   * Compute filtered items based on activeFilterIds.
   * - If no filter is active => all items
   *
   * @returns {HTMLElement[]}
   */
  getFilteredItems() {
    if (!this.activeFilterIds || this.activeFilterIds.length === 0) {
      return this.allItems.slice();
    }

    return this.allItems.filter((el) => {
      const rawFilterId = el.getAttribute('data-filter-id') || '';

      if (this.filterMatchMode === 'multi') {
        const itemFilterIds = rawFilterId
          .split(/\s+/)
          .map(id => normalizeResponsiveSuffix(id))
          .filter(Boolean);

        return itemFilterIds.some(id => this.activeFilterIds.indexOf(id) !== -1);
      }

      const itemFilterId = normalizeResponsiveSuffix(rawFilterId);
      return this.activeFilterIds.indexOf(itemFilterId) !== -1;
    });
  }

  /**
   * Visible count based on paging.
   * @returns {number}
   */
  getVisibleCount() {
    const count = this.currentPage * this.itemsPerPage;
    return count < 0 ? 0 : count;
  }

  /**
   * Apply visibility to items:
   * - hide all items in Container (domAllItems!)
   * - show only filtered items (from scoped) to visibleCount
   *
   * @param {HTMLElement[]} filteredItems
   * @param {number} visibleCount
   * @returns {void}
   */
  applyVisibility(filteredItems, visibleCount) {
    this.domAllItems.forEach((el) => {
      addClass(el, 'hidden');
      /* eslint-disable-next-line no-param-reassign */
      el.style.display = 'none';
    });

    filteredItems.forEach((el, index) => {
      const shouldShow = index < visibleCount;

      if (shouldShow) {
        removeClass(el, 'hidden');
        /* eslint-disable-next-line no-param-reassign */
        el.style.display = '';
      } else {
        addClass(el, 'hidden');
        /* eslint-disable-next-line no-param-reassign */
        el.style.display = 'none';
      }
    });
  }

  /**
   * Show load-more button only if there are more (filtered) items than visible.
   * @param {number} totalFiltered
   * @param {number} visibleCount
   * @returns {void}
   */
  updateLoadMoreVisibility(totalFiltered, visibleCount) {
    if (!this.loadMoreBtn) {
      return;
    }

    const shouldShow = totalFiltered > visibleCount;

    if (shouldShow) {
      removeClass(this.loadMoreBtn, 'hidden');
      this.loadMoreBtn.style.display = '';
    } else {
      addClass(this.loadMoreBtn, 'hidden');
      this.loadMoreBtn.style.display = 'none';
    }
  }
}

export default FilterListController;
